// Copyright (c) StrangeBee 2023

import { useGenericQuery } from '@Queries/genericQueries';
import React, { ReactElement, useMemo, useState } from 'react';
import { SharedOrgOutput, FilterInput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/locale';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { Button } from 'antd';
import THSelector from '@Molecules/THSelector/THSelector';
import { IsChecked } from '@Organisms/IsChecked/IsChecked';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ICONS } from '@Constants/iconsApp';

import './EntitySharingCreate.scss';

const populateFilters = (organisations: string[]): FilterInput[] =>
    organisations.map((organisation) => ({
        field: 'organisationName',
        type: 'string',
        values: organisation,
        operator: 'not',
        describeAttribute: '' as any,
    }));

interface EntitySharingCreateFormProps {
    caseId: string;
    currentOrganisation: string;
    alreadySharedList?: SharedOrgOutput[];
    onCaseShareList: (id: string, filters?: FilterInput[]) => Promise<SharedOrgOutput[]>;
    stateKeys: { selectedKeys: SharedOrgOutput[]; setSelectedKeys: (selectedKeys: SharedOrgOutput[]) => void };
}

const EntitySharingCreateForm = ({
    caseId,
    currentOrganisation,
    onCaseShareList,
    alreadySharedList = [],
    stateKeys,
}: EntitySharingCreateFormProps): ReactElement => {
    const { selectedKeys, setSelectedKeys } = stateKeys;

    const organisations = useMemo(
        () => [...alreadySharedList.map(({ organisationName }) => organisationName), currentOrganisation],
        [alreadySharedList, currentOrganisation],
    );

    const { data: shareList } = useGenericQuery({
        fetchCallback: () => onCaseShareList(caseId, populateFilters(organisations)),
        key: ['sharings-create'],
    });

    return (
        <div>
            {!!shareList?.length && (
                <THSelector<SharedOrgOutput>
                    dataSource={shareList}
                    refKey="organisationName"
                    value={selectedKeys}
                    onChange={(selectedRowsKeys): void => setSelectedKeys(selectedRowsKeys as SharedOrgOutput[])}
                >
                    {({ organisationName, profileName }, selected = false): ReactElement => (
                        <div className="entity-sharing-create__list-item">
                            <IsChecked checked={selected} className="entity-sharing-create__list-item__selected" />
                            {`${organisationName} (${profileName})`}
                        </div>
                    )}
                </THSelector>
            )}
        </div>
    );
};

interface EntitySharingCreateProps {
    caseId: string;
    currentOrganisation: string;
    entityId: string;
    onCaseShareList: (id: string, filters?: FilterInput[]) => Promise<SharedOrgOutput[]>;
    onAdd: (organisations: string[], id: string) => void;
    alreadySharedList?: SharedOrgOutput[];
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const EntitySharingCreate = ({
    caseId,
    currentOrganisation,
    entityId,
    onCaseShareList,
    onAdd,
    alreadySharedList = [],
    triggerElem,
    visible,
    onVisibleChange,
}: EntitySharingCreateProps): ReactElement => {
    const i = useLocalMessage();

    const [selectedKeys, setSelectedKeys] = useState<SharedOrgOutput[]>([]);

    const submitHandler = (toggleDrawer: (...args: any[]) => any): void => {
        onAdd?.(
            selectedKeys.map(({ organisationName }) => organisationName),
            entityId,
        );
        setSelectedKeys([]);
        toggleDrawer();
    };

    return (
        <THDrawer
            title={i('sharing.add')}
            visible={visible}
            afterVisibleChange={(visible) => {
                onVisibleChange?.(visible);
            }}
            triggerElem={triggerElem || <Button type="dashed" icon={<ICONS.PlusIcon />} shape="circle" size="small" />}
            footer={(toggleDrawer): ReactElement => (
                <THButton
                    type="primary"
                    block
                    onClick={(): void => submitHandler(toggleDrawer)}
                    disabled={!selectedKeys.length}
                >
                    {i('sharing.add')}
                </THButton>
            )}
        >
            {onCaseShareList && (
                <EntitySharingCreateForm
                    currentOrganisation={currentOrganisation}
                    onCaseShareList={onCaseShareList}
                    alreadySharedList={alreadySharedList}
                    caseId={caseId}
                    stateKeys={{ selectedKeys, setSelectedKeys }}
                />
            )}
        </THDrawer>
    );
};
